import { useState } from "react";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errorText, fetcher } from "@/lib/api";
import { num } from "@/lib/format";

const HERO = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: stats } = useSWR("/public/stats", fetcher);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await login(username, password); navigate("/"); }
    catch (err) { setError(errorText(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-slate-950">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="KAZO flagship store" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="font-heading font-extrabold tracking-[0.3em] text-xl">KAZO</div>
          <div className="space-y-6 max-w-lg">
            <h1 className="font-heading text-4xl font-extrabold leading-tight">Every SKU. Every store. Every decision — computed, not guessed.</h1>
            <p className="text-slate-300 text-sm leading-relaxed">Sales, inventory, stock health and recommendations at the Variant × Size × Location × Date grain, reconciled against an append-only ledger.</p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              {[[stats ? num(stats.skus) : "—", "SKUs tracked"], [stats ? num(stats.locations) : "—", "Locations"], ["7", "Health states"]].map(([v, l]) => (
                <div key={l} className="border-l-2 border-[#D4AF37] pl-3"><div className="font-mono text-2xl font-semibold">{v}</div><div className="text-slate-400 text-xs">{l}</div></div>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-400">Powered by <span className="text-white font-semibold">Fundle</span> · Merchandise Intelligence OS</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-slate-50 dark:bg-slate-950">
        <div className="w-full max-w-sm">
          <div className="lg:hidden font-heading font-extrabold tracking-[0.3em] text-xl mb-8">KAZO</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1 mb-8">Use your KAZO merchandise credentials.</p>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="eyebrow block mb-1.5" htmlFor="username">Username</label>
              <input id="username" data-testid="login-username-input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required
                className="w-full h-11 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60" placeholder="planner" />
            </div>
            <div>
              <label className="eyebrow block mb-1.5" htmlFor="password">Password</label>
              <input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required
                className="w-full h-11 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60" placeholder="••••••••" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-md px-3 py-2">{error}</div>}
            <button data-testid="login-submit-btn" disabled={busy} className="w-full h-11 rounded-md bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-semibold text-sm hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors disabled:opacity-60">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-6 text-xs text-slate-500">Access is provisioned by your KAZO administrator.</div>
          <div data-testid="login-powered-by" className="mt-12 text-xs text-slate-400">Powered by <span className="font-semibold text-slate-600 dark:text-slate-300">Fundle</span></div>
        </div>
      </div>
    </div>
  );
}
